/**
 * Channel classification, JavaScript side.
 *
 * A direct mirror of src/Classification/Classifier.php. Both are driven by the same rule
 * table (src/Classification/channel-rules.json) and both are executed against the same
 * fixture (tests/fixtures/classifier-cases.json) in CI, so a change to one that is not
 * made to the other fails the build rather than quietly producing two answers.
 *
 * The client's answer is never authoritative. The server re-runs this same logic over
 * the raw signals and discards whatever the browser decided -- this copy exists so the
 * form can be populated before the round trip completes, and so a visitor whose REST
 * call is blocked still gets something.
 */

const MAX_LENGTH = 255;

export const CHANNELS = Object.freeze( {
	PAID_SEARCH: 'paid_search',
	PAID_SOCIAL: 'paid_social',
	PAID_DISPLAY: 'paid_display',
	ORGANIC_SEARCH: 'organic_search',
	ORGANIC_SOCIAL: 'organic_social',
	EMAIL: 'email',
	AFFILIATE: 'affiliate',
	REFERRAL: 'referral',
	OTHER: 'other',
	DIRECT: 'direct',
} );

/**
 * Truncates to the canonical field contract length.
 *
 * @param {string} value Raw value.
 * @return {string} Value capped at 255 characters.
 */
function truncate( value ) {
	const string = String( value == null ? '' : value );
	return string.length <= MAX_LENGTH ? string : Array.from( string ).slice( 0, MAX_LENGTH ).join( '' );
}

/**
 * Normalises a parameter bag: lowercased keys, trimmed string values.
 *
 * @param {object|URLSearchParams} params Raw parameters.
 * @return {object} Normalised parameters.
 */
export function normaliseParams( params ) {
	const out = {};

	if ( params instanceof URLSearchParams ) {
		for ( const [ key, value ] of params.entries() ) {
			const name = key.trim().toLowerCase();
			// First occurrence wins, matching the PHP side.
			if ( ! ( name in out ) ) {
				out[ name ] = String( value ).trim();
			}
		}

		return out;
	}

	for ( const [ key, value ] of Object.entries( params || {} ) ) {
		const raw = Array.isArray( value ) ? value[ 0 ] : value;

		if ( raw === null || raw === undefined || typeof raw === 'object' ) {
			continue;
		}

		out[ key.trim().toLowerCase() ] = String( raw ).trim();
	}

	return out;
}

/**
 * Builds the signal object the classifier takes.
 *
 * @param {string} url      Landing URL.
 * @param {string} referrer Referring URL.
 * @return {{params: object, referrer: string, landing: string}} Signals.
 */
export function signalsFromUrl( url, referrer = '' ) {
	let params = {};

	try {
		params = normaliseParams( new URL( url ).searchParams );
	} catch {
		params = {};
	}

	return { params, referrer: String( referrer || '' ).trim(), landing: String( url || '' ).trim() };
}

/**
 * The referrer's hostname, normalised.
 *
 * @param {string} referrer Referring URL.
 * @return {string} Hostname, or an empty string.
 */
function referrerHost( referrer ) {
	if ( ! referrer ) {
		return '';
	}

	try {
		return new URL( referrer ).hostname.toLowerCase().replace( /\.+$/, '' );
	} catch {
		return '';
	}
}

/**
 * Whether the referrer is an Android app (android-app://<package>/) rather than a page.
 *
 * Mirrors TouchSignals::isAppReferrer(). The "hostname" is then a package name, which is
 * looked up in appReferrers and never run through the domain rules.
 *
 * @param {string} referrer Referring URL.
 * @return {boolean} True for an app referrer.
 */
function isAppReferrer( referrer ) {
	return /^android-app:\/\//i.test( String( referrer || '' ) );
}

/**
 * First non-empty value among the given parameter names.
 *
 * @param {object}   params Normalised parameters.
 * @param {string[]} names  Parameter names in precedence order.
 * @return {string} The value, or an empty string.
 */
function firstParam( params, names ) {
	for ( const name of names ) {
		const value = params[ String( name ).toLowerCase() ];

		if ( value ) {
			return value;
		}
	}

	return '';
}

/**
 * Finds the click ID present in a set of parameters.
 *
 * @param {object} params Normalised parameters.
 * @param {object} rules  Rule accessor.
 * @return {{param: string, value: string, definition: object}|null} Click ID, or null.
 */
export function detectClickId( params, rules ) {
	for ( const [ param, definition ] of rules.clickIds() ) {
		const value = params[ param ];

		if ( value ) {
			return { param, value, definition };
		}
	}

	return null;
}

/**
 * BEHAVIOURAL RULE 2: resolves the click ID from the most recent touch that HAD one.
 *
 * A June paid click followed by an August email return has a correct last touch of
 * `email` and still needs June's gclid -- it is the only thing that lets the conversion
 * be uploaded back to Google Ads. Reading the click ID off the last touch loses the
 * conversion with no error anywhere.
 *
 * @param {Array<object>} path Touch path, oldest first.
 * @return {{clickId: string, clickType: string, touchIndex: number}|null} Resolved click ID.
 */
export function resolveClickIdFromPath( path ) {
	const list = Array.isArray( path ) ? path : [];

	for ( let i = list.length - 1; i >= 0; i-- ) {
		const touch = list[ i ];

		if ( ! touch ) {
			continue;
		}

		const clickId = String( touch.click_id || touch.clickId || '' ).trim();

		if ( ! clickId ) {
			continue;
		}

		return {
			clickId,
			clickType: String( touch.click_type || touch.clickType || '' ).trim(),
			touchIndex: i,
		};
	}

	return null;
}

/**
 * Whether a hostname is the given brand followed only by a country-code-style TLD.
 *
 * google.com, www.google.co.uk and news.google.com.au all match `google`;
 * google.evil.com does not, because `evil` is not TLD-shaped. A deliberate
 * approximation of a public suffix list that errs towards under-matching and never
 * treats co.uk as a registrable domain.
 *
 * @param {string} host  Hostname.
 * @param {string} token Brand label.
 * @return {boolean} True on match.
 */
function matchesBrandHost( host, token ) {
	const labels = host.split( '.' );
	const index = labels.indexOf( token );

	if ( index === -1 ) {
		return false;
	}

	const tail = labels.slice( index + 1 );

	if ( tail.length < 1 || tail.length > 2 ) {
		return false;
	}

	return tail.every( ( label ) => /^[a-z]{2,3}$/.test( label ) );
}

/**
 * Maps a hostname to a platform.
 *
 * @param {string} host  Hostname.
 * @param {object} rules Rule accessor.
 * @return {string|null} Platform, or null.
 */
function platformForHost( host, rules ) {
	const value = String( host || '' ).trim().toLowerCase().replace( /\.+$/, '' );

	if ( ! value ) {
		return null;
	}

	const hosts = rules.referrerHosts();

	if ( hosts[ value ] ) {
		return hosts[ value ];
	}

	// Webmail by first label, before suffix and brand matching. See Classifier::platformForHost().
	if ( value.includes( '.' ) && rules.emailHostPrefixes().includes( value.split( '.' )[ 0 ] ) ) {
		return 'email';
	}

	let best = null;
	let bestLength = 0;

	for ( const [ domain, platform ] of Object.entries( hosts ) ) {
		if ( value.endsWith( '.' + domain ) && domain.length > bestLength ) {
			best = platform;
			bestLength = domain.length;
		}
	}

	if ( best ) {
		return best;
	}

	for ( const [ token, platform ] of Object.entries( rules.brandHostTokens() ) ) {
		if ( matchesBrandHost( value, token ) ) {
			return platform;
		}
	}

	return null;
}

/**
 * Maps a utm_source token to a platform.
 *
 * @param {string} source Tagged source.
 * @param {object} rules  Rule accessor.
 * @return {string|null} Platform, or null.
 */
function platformForSourceToken( source, rules ) {
	const token = String( source || '' ).trim().toLowerCase();
	const tokens = rules.sourceTokens();

	if ( tokens[ token ] ) {
		return tokens[ token ];
	}

	if ( token.includes( '.' ) ) {
		const platform = platformForHost( token, rules );

		if ( platform ) {
			return platform;
		}
	}

	return null;
}

/**
 * Buckets a tagged medium. Returns null when untagged, 'unknown' when unrecognised.
 *
 * @param {string} medium Tagged medium.
 * @param {object} rules  Rule accessor.
 * @return {string|null} Medium class.
 */
function classifyMedium( medium, rules ) {
	const token = String( medium || '' ).trim().toLowerCase();

	if ( ! token ) {
		return null;
	}

	for ( const className of [ 'display', 'paid', 'organicSearch', 'organicSocial', 'email', 'affiliate', 'referral' ] ) {
		if ( rules.mediums( className ).includes( token ) ) {
			return className;
		}
	}

	return 'unknown';
}

/**
 * Channel for a recognised tagged medium.
 *
 * @param {string}      mediumClass Bucketed medium.
 * @param {string|null} platform    Resolved platform.
 * @return {string} Channel.
 */
function channelForTaggedMedium( mediumClass, platform ) {
	switch ( mediumClass ) {
		case 'display':
			return CHANNELS.PAID_DISPLAY;
		case 'email':
			return CHANNELS.EMAIL;
		case 'affiliate':
			return CHANNELS.AFFILIATE;
		case 'organicSearch':
			return CHANNELS.ORGANIC_SEARCH;
		case 'organicSocial':
			return CHANNELS.ORGANIC_SOCIAL;
		case 'referral':
			return CHANNELS.REFERRAL;
		case 'paid':
			switch ( platform ) {
				case 'search':
				case 'ai':
					return CHANNELS.PAID_SEARCH;
				case 'social':
					return CHANNELS.PAID_SOCIAL;
				case 'display':
					return CHANNELS.PAID_DISPLAY;
				case 'affiliate':
					return CHANNELS.AFFILIATE;
				default:
					// Paid spend against a platform the rule table cannot name. Guessing
					// paid_search would credit search for display or partner spend, so
					// this becomes `other` and raises an admin notice instead. The click
					// ID is captured either way, so offline upload is unaffected.
					return CHANNELS.OTHER;
			}
		default:
			return CHANNELS.OTHER;
	}
}

/**
 * Channel implied by a click ID with no tagged medium.
 *
 * @param {object}      click    Detected click ID.
 * @param {string|null} platform Resolved platform.
 * @return {string} Channel.
 */
function channelForClickId( click, platform ) {
	const paid = Boolean( click.definition && click.definition.paid );
	const surface = platform || ( click.definition && click.definition.surface ) || null;

	/*
	 * BEHAVIOURAL RULE 3: fbclid is not evidence of paid. Meta appends it to organic post
	 * links, shared links and Messenger links, not only to ads. Treating its presence as
	 * a paid click inflates paid_social with traffic nobody bought. It is the only click
	 * ID in the table with paid=false; an explicitly tagged paid medium still overrides
	 * this, on a path that never reaches here.
	 */
	if ( ! paid ) {
		switch ( surface ) {
			case 'social':
				return CHANNELS.ORGANIC_SOCIAL;
			case 'search':
			case 'ai':
				return CHANNELS.ORGANIC_SEARCH;
			default:
				return CHANNELS.REFERRAL;
		}
	}

	switch ( surface ) {
		case 'search':
		case 'ai':
			return CHANNELS.PAID_SEARCH;
		case 'social':
			return CHANNELS.PAID_SOCIAL;
		case 'display':
			return CHANNELS.PAID_DISPLAY;
		case 'affiliate':
			return CHANNELS.AFFILIATE;
		default:
			return CHANNELS.OTHER;
	}
}

/**
 * Derives a medium when none was tagged.
 *
 * @param {object|null} click   Detected click ID.
 * @param {string}      channel Resolved channel.
 * @return {string} Derived medium, or an empty string.
 */
function deriveMedium( click, channel ) {
	if ( click && click.definition && click.definition.medium ) {
		return String( click.definition.medium );
	}

	switch ( channel ) {
		case CHANNELS.ORGANIC_SEARCH:
			return 'organic';
		case CHANNELS.ORGANIC_SOCIAL:
			return 'social';
		case CHANNELS.EMAIL:
			return 'email';
		case CHANNELS.AFFILIATE:
			return 'affiliate';
		case CHANNELS.REFERRAL:
			return 'referral';
		case CHANNELS.PAID_DISPLAY:
			return 'display';
		default:
			return '';
	}
}

/**
 * Classifies a page view.
 *
 * @param {{params: object, referrer: string, landing: string}} signals Page view signal.
 * @param {object}                                             rules   Rule accessor.
 * @return {object} Classification.
 */
export function classify( signals, rules ) {
	const params = normaliseParams( signals.params );

	const source = firstParam( params, rules.campaignParams( 'source' ) );
	const medium = firstParam( params, rules.campaignParams( 'medium' ) );
	const campaign = firstParam( params, rules.campaignParams( 'campaign' ) );
	const content = firstParam( params, rules.campaignParams( 'content' ) );
	const term = firstParam( params, rules.campaignParams( 'term' ) );

	const click = detectClickId( params, rules );

	const host = referrerHost( signals.referrer );
	const fromApp = isAppReferrer( signals.referrer );

	// A sign-in or payment provider is treated like no referrer at all.
	const externalReferrer = host !== '' && ! rules.isInternalHost( host ) && ! rules.isExcludedReferrer( host );

	const hasCampaignParams = Boolean( source || medium || campaign || content || term );

	/*
	 * BEHAVIOURAL RULE 1, base case. No UTM, no click ID, no external referrer means
	 * there is nothing here to attribute. It resolves to `direct`, and a `direct`
	 * classification is what isRecordableTouch() refuses to record -- which is what
	 * stops a visitor browsing four internal pages after an ad click from overwriting
	 * their own last touch with direct/(none).
	 */
	if ( ! hasCampaignParams && ! click && ! externalReferrer ) {
		return {
			channel: CHANNELS.DIRECT,
			source: '',
			medium: '',
			campaign: '',
			content: '',
			term: '',
			clickId: '',
			clickType: '',
			synthesised: false,
		};
	}

	let platform = null;

	if ( source ) {
		platform = platformForSourceToken( source, rules );
	}

	if ( ! platform && click && click.definition && click.definition.surface ) {
		platform = click.definition.surface;
	}

	if ( ! platform && externalReferrer ) {
		platform = fromApp ? rules.appReferrers()[ host ] || null : platformForHost( host, rules );
	}

	const mediumClass = classifyMedium( medium, rules );

	let channel;

	if ( mediumClass && mediumClass !== 'unknown' ) {
		// A tagged medium is marketing telling us what they bought. It wins.
		channel = channelForTaggedMedium( mediumClass, platform );
	} else if ( mediumClass === 'unknown' ) {
		// Tagged, but with a medium nobody recognises. That is a tagging defect.
		channel = CHANNELS.OTHER;
	} else if ( click ) {
		channel = channelForClickId( click, platform );
	} else if ( platform ) {
		switch ( platform ) {
			case 'search':
			case 'ai':
				// AI assistants are an acquisition channel in their own right and are
				// grouped with organic search rather than dumped into referral.
				channel = CHANNELS.ORGANIC_SEARCH;
				break;
			case 'social':
				channel = CHANNELS.ORGANIC_SOCIAL;
				break;
			case 'email':
				channel = CHANNELS.EMAIL;
				break;
			case 'affiliate':
				channel = CHANNELS.AFFILIATE;
				break;
			case 'display':
				// A referral from an ad-serving network is a paid placement.
				channel = CHANNELS.PAID_DISPLAY;
				break;
			case 'referral':
				// A host named as a plain referral, e.g. docs.google.com.
				channel = CHANNELS.REFERRAL;
				break;
			default:
				channel = CHANNELS.OTHER;
		}
	} else if ( externalReferrer ) {
		channel = CHANNELS.REFERRAL;
	} else {
		// Campaign params present but empty of anything placeable, e.g. utm_campaign alone.
		channel = CHANNELS.OTHER;
	}

	let finalSource = source;
	let finalMedium = medium;
	let synthesised = false;

	if ( ! finalSource ) {
		const derived = ( click && click.definition && click.definition.source ) || ( externalReferrer ? host : '' );

		if ( derived ) {
			finalSource = String( derived );
			synthesised = true;
		}
	}

	if ( ! finalMedium ) {
		const derived = deriveMedium( click, channel );

		if ( derived ) {
			finalMedium = derived;
			synthesised = true;
		}
	}

	return {
		channel,
		source: truncate( finalSource ),
		medium: truncate( finalMedium ),
		campaign: truncate( campaign ),
		content: truncate( content ),
		term: truncate( term ),
		clickId: click ? truncate( click.value ) : '',
		clickType: click ? click.param : '',
		synthesised,
	};
}

/**
 * The fingerprint used for touch deduplication.
 *
 * Content and term are deliberately excluded: rotating ad creative within one campaign
 * is not a new touch.
 *
 * @param {object} classification Classification result.
 * @return {string} Fingerprint.
 */
export function fingerprint( classification ) {
	return [
		String( classification.source || '' ).toLowerCase(),
		String( classification.medium || '' ).toLowerCase(),
		String( classification.campaign || '' ).toLowerCase(),
		String( classification.clickId || '' ),
	].join( '|' );
}
